import { ofType } from 'redux-observable'
import { EMPTY, interval, merge, of, Subject } from 'rxjs'
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators'

import { combineEpicsIsolated } from 'utils/epicRegistry'
import { beautifyObjectKeys } from 'utils/helpers'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import {
  DELETE_ATO_CAMPAIGN_BANNER_ASYNC,
  DISABLE_PHISHING_SOC_TRIAL_ASYNC,
  ENABLE_PHISHING_SOC_TRIAL_ASYNC,
  GENERATE_STATUS_TOKEN_ASYNC,
  GET_AGENT_STATUS_BATCH_ASYNC,
  GET_AGENT_TOKEN_ASYNC,
  GET_ATO_CAMPAIGN_BANNER_ASYNC,
  GET_BILLING_STATUS_ASYNC,
  GET_PHISHING_SOC_TRIAL_ASYNC,
  POST_ATO_CAMPAIGN_CONTACT_ME_ASYNC,
  REQUEST_THEMIS_SUMMARY_ASYNC,
} from 'containers/Agents/constants'
import { FETCH_APP_DATA_ASYNC } from 'containers/App/constants'
import { selectActualFeatureFlags, selectIsStaticSalesDemo } from 'containers/App/selectors'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { INCIDENT_TYPE } from 'containers/ResolutionCenter/constants'
import { LIST_REQUEST_ASYNC } from 'containers/ResolutionCenter/List/constants'
import {
  selectReports,
  selectSelectedReportId,
  selectSelectedReportType,
} from 'containers/ResolutionCenter/List/selectors'
import { themisAnalysisFailure } from 'containers/ResolutionCenter/Report/actions'
import { REPORT_REQUEST_ASYNC } from 'containers/ResolutionCenter/Report/constants'
import { selectThemisInsufficientCredits } from 'containers/ResolutionCenter/Report/selectors'
import { addToast } from 'containers/Toasts/actions'

import { FillSparkle } from 'components/lib/DesignSystem/Icons'
import { ANALYSIS_CONTAINER_ID } from 'components/pages/resolutionCenter/Report/components/constants'

import { selectIsCreditsExhausted } from './creditSelectors'
import {
  selectAgentStatusBatch,
  selectAgentStatusBatchError,
  selectCanUsePhishingSocAgent,
  selectCanUseSelfPhishingSocAgent,
  selectIncidentIdToEidMap,
  selectIncidentIdToReportType,
  selectStatusHostname,
  selectStatusToken,
  selectStatusTokenError,
} from './selectors'
import { shouldAutoRecoverFailedInvestigation } from './shouldAutoRecoverFailedInvestigation'
// Track running streaming analyses to avoid duplicate calls
const runningStreamingAnalyses = new Set()
const STATUS_POLL_INTERVAL_MS = 10_000

// getStreamingAnalysis is a fire-and-forget async call (its own component handles
// the actual streaming); its failure handler used to call `store.dispatch`
// directly, decoupled from this epic's own output stream/timing. There is no
// `store` under redux-observable 1.x (only `state$`, which has no dispatch), so
// bridge that async, out-of-band dispatch into the epic's returned Observable via
// this Subject instead.
const themisAnalysisFailure$ = new Subject()

const getAgentTokenEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_AGENT_TOKEN_ASYNC.INITIATE, REPORT_REQUEST_ASYNC.SUCCESS),
    filter(() => selectCanUsePhishingSocAgent(state$.value)),
    mergeMap((action) => {
      const state = state$.value
      const companyFilter = selectCompanyFilter(state)
      // Prefer the action's own report over the list's selection, which can point
      // at a different/unrelated row (new Incidents page, or a different row picked
      // while this incident is open).
      const reportId =
        action.payload.data.reportId ||
        action.payload.data.report?.id ||
        selectSelectedReportId(state)
      // Only trust the list's type as a fallback when the list's selection is
      // actually describing this same reportId.
      const listSelectedReportId = selectSelectedReportId(state)
      const reportType =
        action.payload.data.reportType ||
        action.payload.data.report?.reportType ||
        (listSelectedReportId != null &&
        reportId != null &&
        String(listSelectedReportId) === String(reportId)
          ? selectSelectedReportType(state)
          : null)
      // The Phishing SOC Agent only supports email incidents. Skip the token
      // fetch for Teams/MSFT/ATO reports so we never hit the report_id/report_type
      // collision on the backend.
      if (reportType !== INCIDENT_TYPE.MAIL) {
        return EMPTY
      }
      return api.agents.generateAgentToken(reportId, companyFilter?.value, reportType).pipe(
        map((response) => {
          // add caching
          // Map TokenResponse to expected format
          const tokenData = response.response || response
          const mappedResponse = {
            agentToken: tokenData?.token,
            agentHostname: tokenData?.themisHostname || tokenData?.agentHostname,
            expiresAt: tokenData?.expires_at,
            eid: tokenData?.eid,
            reportId,
            reportType,
          }
          return GET_AGENT_TOKEN_ASYNC.onSuccess(mappedResponse)
        }),
        // 403: the report's company (e.g. an MSP child listed under the MSP's incidents)
        // may lack phishing_soc_access/flags/phish_soc, none of which the FE holds for
        // that company, so the backend is the authority (themis_token.py).
        catchError((error) =>
          of(epicsErrorHandler(GET_AGENT_TOKEN_ASYNC.onError, error, [403, 404]))
        ),
        takeUntil(action$.pipe(ofType(GET_AGENT_TOKEN_ASYNC.CANCEL)))
      )
    })
  )

const generateAgentStatusTokenEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GENERATE_STATUS_TOKEN_ASYNC.INITIATE),
    filter(() => selectCanUseSelfPhishingSocAgent(state$.value)),
    exhaustMap(() =>
      api.agents.generateStatusToken().pipe(
        map(({ response }) => {
          const mappedResponse = {
            agentToken: response?.token,
            agentHostname: response?.themisHostname || response?.agentHostname,
            expiresAt: response?.expires_at,
          }
          return GENERATE_STATUS_TOKEN_ASYNC.onSuccess(mappedResponse)
        }),
        catchError((error) =>
          of(epicsErrorHandler(GENERATE_STATUS_TOKEN_ASYNC.onError, error, [403]))
        ),
        takeUntil(action$.pipe(ofType(GENERATE_STATUS_TOKEN_ASYNC.CANCEL)))
      )
    )
  )

const getAgentStatusBatchEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_AGENT_STATUS_BATCH_ASYNC.INITIATE,
      LIST_REQUEST_ASYNC.SUCCESS,
      GET_AGENT_TOKEN_ASYNC.SUCCESS
    ),
    filter((action) => {
      const state = state$.value
      if (!selectCanUseSelfPhishingSocAgent(state)) {
        return false
      }
      // Skip if token success didn't add a new eid
      if (action.type === GET_AGENT_TOKEN_ASYNC.SUCCESS) {
        const eid = action.payload.eid
        const reportId = action.payload.reportId
        if (!eid || !reportId) return false
        const eidsById = selectIncidentIdToEidMap(state)
        return eidsById[reportId] === eid // reducer already ran, check it was added
      }
      return true
    }),
    switchMap(() => {
      // Polling observable: fires immediately and then every 10 seconds
      // Also restarts on successful token generation
      const statusPoll$ = merge(
        action$.pipe(
          ofType(GENERATE_STATUS_TOKEN_ASYNC.SUCCESS),
          map(() => 0)
        ),
        interval(STATUS_POLL_INTERVAL_MS)
      ).pipe(startWith(0))

      return merge(
        themisAnalysisFailure$,
        statusPoll$.pipe(
          exhaustMap(() => {
            const state = state$.value
            const statusToken = selectStatusToken(state)
            const hostname = selectStatusHostname(state)
            const isStaticDemo = selectIsStaticSalesDemo(state)
            const statusBatchError = selectAgentStatusBatchError(state)

            if (
              statusBatchError &&
              (isStaticDemo || statusBatchError?.status === 405 || statusBatchError?.status === 403)
            ) {
              return EMPTY
            }

            if (!statusToken) {
              const statusTokenError = selectStatusTokenError(state)

              if (
                statusTokenError &&
                (isStaticDemo ||
                  statusTokenError?.status === 405 ||
                  statusTokenError?.status === 403)
              ) {
                return EMPTY
              }

              return of(GENERATE_STATUS_TOKEN_ASYNC.initiate())
            }
            const eidsById = selectIncidentIdToEidMap(state)

            if (Object.keys(eidsById).length === 0) {
              return EMPTY
            }
            const previousStatusBatch = selectAgentStatusBatch(state)
            return api.agents
              .getAgentStatusBatch(statusToken, Object.values(eidsById), hostname)
              .pipe(
                mergeMap((response) => {
                  // Extract status batch (handle both wrapped and unwrapped responses)
                  const statusBatch = response.response || response
                  const actions = [GET_AGENT_STATUS_BATCH_ASYNC.onSuccess(statusBatch)]

                  if (statusBatch && typeof statusBatch === 'object') {
                    const now = Date.now()
                    const TOAST_WINDOW_MS = 10_000 // 10 seconds

                    const doneEids = Object.keys(statusBatch).filter((eid) => {
                      const currentStatusObj = statusBatch[eid]
                      const currentStatus = currentStatusObj?.status
                      const previousStatusObj = previousStatusBatch?.[eid]
                      const previousStatus = previousStatusObj?.status

                      // Only show toast if:
                      // 1. Current status is 'done'
                      // 2. Triggered by current user
                      // 3. Within 10 seconds window from updatedAt
                      // 4. Previous status exists and was NOT 'done' (transitioned from inProgress/failed to done)
                      // This prevents duplicate toasts on subsequent polls and on page refresh
                      if (currentStatus !== 'done') {
                        return false
                      }

                      // Check if triggered by current user
                      if (!currentStatusObj?.triggeredByCurrentUser) {
                        return false
                      }

                      // Check if within 10 seconds window
                      if (currentStatusObj?.updatedAt) {
                        const updatedAt = new Date(currentStatusObj.updatedAt).getTime()
                        const timeDiff = now - updatedAt
                        if (timeDiff < 0 || timeDiff > TOAST_WINDOW_MS) {
                          return false
                        }
                      } else {
                        // If updatedAt is missing, don't show toast
                        return false
                      }

                      // Check transition from non-done to done
                      if (!previousStatusBatch) {
                        return true
                      }

                      return previousStatus !== undefined && previousStatus !== 'done'
                    })

                    // Add toast actions for each newly 'done' eid
                    const currentState = state$.value
                    let selectedReportId

                    try {
                      selectedReportId = selectSelectedReportId(currentState)
                    } catch {
                      selectedReportId = null
                    }

                    doneEids.forEach((eid) => {
                      const targetId = Object.keys(eidsById).find((id) => eidsById[id] === eid)

                      const isAnalysisTabOpen = Boolean(
                        document.getElementById(`${ANALYSIS_CONTAINER_ID}-${targetId}`)
                      )

                      // Skip toast if user is already viewing this incident with analysis tab open
                      // For attackdetails: check if URL includes the specific targetId
                      // For classified/unclassified: check if the selected report matches this targetId
                      // Convert to numbers for type-safe comparison (Object.keys returns strings)
                      const isViewingThisIncident =
                        matchURL(`${URL_CONST.incidents.attackdetails}/${targetId}`) ||
                        ((matchURL(URL_CONST.incidents.classified) ||
                          matchURL(URL_CONST.incidents.unclassified)) &&
                          selectedReportId != null &&
                          Number(selectedReportId) === Number(targetId))

                      if (isViewingThisIncident && isAnalysisTabOpen) {
                        return
                      }

                      actions.push(
                        addToast({
                          title: 'Phishing SOC Agent',
                          message: 'Deep investigation completed!',
                          id: `phish-agentic-soc-status-done-${targetId}`,
                          type: 'neutral',
                          icon: FillSparkle,
                          button: {
                            id: `phish-agentic-soc-status-done-${targetId}-button`,
                            text: 'Go to incident >',
                            onClick: () => {
                              const socPath = '?soc=1'
                              const url = targetId
                                ? `${URL_CONST.incidents.attackdetails}/mail/${targetId}${socPath}`
                                : `${URL_CONST.incidents.classified}${socPath}`
                              window.open(url, '_blank')
                            },
                          },
                        })
                      )
                    })

                    // Check for failed statuses and trigger streaming analysis
                    const failedEids = Object.keys(statusBatch).filter(
                      (eid) => statusBatch[eid]?.status === 'failed'
                    )

                    // Clean up tracking: remove reportIds that are no longer in the list or no longer failed
                    const currentReportIds = new Set(Object.keys(eidsById))
                    runningStreamingAnalyses.forEach((reportId) => {
                      if (!currentReportIds.has(reportId)) {
                        // ReportId no longer in the list, remove from tracking
                        runningStreamingAnalyses.delete(reportId)
                      } else {
                        // ReportId still in list, check if status is no longer failed
                        const eid = eidsById[reportId]
                        const currentStatusObj = statusBatch[eid]
                        const currentStatus = currentStatusObj?.status
                        if (currentStatus && currentStatus !== 'failed') {
                          runningStreamingAnalyses.delete(reportId)
                        }
                      }
                    })

                    // Auto-recover failed investigations for the viewed incident only,
                    // and only on a live transition into `failed` (not sticky failed
                    // after refresh). Status batch has no failure reason, so credit
                    // blocks are enforced via Redux / credits selectors (IC-49715).
                    const creditsExhausted = selectIsCreditsExhausted(currentState)

                    failedEids.forEach((eid) => {
                      // Find the reportId (id) for this eid
                      const reportId = Object.keys(eidsById).find((id) => eidsById[id] === eid)

                      if (!reportId || runningStreamingAnalyses.has(reportId)) return

                      const isViewingThisIncident =
                        matchURL(`${URL_CONST.incidents.attackdetails}/${reportId}`) ||
                        ((matchURL(URL_CONST.incidents.classified) ||
                          matchURL(URL_CONST.incidents.unclassified)) &&
                          selectedReportId != null &&
                          Number(selectedReportId) === Number(reportId))

                      const previousStatus = previousStatusBatch?.[eid]?.status
                      const insufficientCredits =
                        selectThemisInsufficientCredits(reportId)(currentState)

                      if (
                        !shouldAutoRecoverFailedInvestigation({
                          previousStatus,
                          isViewing: isViewingThisIncident,
                          insufficientCredits,
                          creditsExhausted,
                        })
                      ) {
                        return
                      }

                      // Only recover an id whose stored type (token request or list row) is MAIL.
                      const reportType = selectIncidentIdToReportType(currentState)?.[reportId]
                      if (reportType !== INCIDENT_TYPE.MAIL) return

                      // Mark as running
                      runningStreamingAnalyses.add(reportId)

                      // Trigger streaming analysis (fire and forget - component handles the streaming).
                      // getStreamingAnalysis is a plain async function (a Promise), not an
                      // Observable, so this stays native Promise then/catch (not catchError/.pipe).
                      const markOutOfCredits = () => {
                        // A 402 is terminal: the status stays 'failed' until credits
                        // are added, so keep the reportId marked as running or every
                        // 10s poll would re-fire the request forever. Align Redux so
                        // Analysis and this epic share the durable block, and refetch
                        // billing/status so the used/total counter is not left stale.
                        themisAnalysisFailure$.next(
                          themisAnalysisFailure(reportId, 'Insufficient credits')
                        )
                        themisAnalysisFailure$.next(GET_BILLING_STATUS_ASYNC.initiate())
                      }

                      api.resolutionCenter
                        .getStreamingAnalysis(reportId, reportType)
                        .then((response) => {
                          if (response?.status === 402) markOutOfCredits()
                        })
                        .catch((error) => {
                          if (error?.message === 'Insufficient credits') {
                            markOutOfCredits()
                            return
                          }
                          // Remove from running set on transient errors so a later live
                          // transition into failed can retry.
                          runningStreamingAnalyses.delete(reportId)
                          // Silently handle errors - we don't want to show toasts for auto-retries
                        })
                    })
                  }

                  return of(...actions)
                }),
                catchError((error) => {
                  if (error.status === 401) {
                    return of(GENERATE_STATUS_TOKEN_ASYNC.initiate())
                  }
                  return of(epicsErrorHandler(GET_AGENT_STATUS_BATCH_ASYNC.onError, error))
                })
              )
          }),
          takeUntil(action$.pipe(ofType(GET_AGENT_STATUS_BATCH_ASYNC.CANCEL)))
        )
      )
    })
  )

// billing/status lives in the statically-mounted Agents slice so it's reachable
// on the incidents route (not just /settings). It does NOT use the agent token —
// it's a members-api call scoped by the company filter.
//
// `REPORT_REQUEST_ASYNC.SUCCESS` (an incident was opened) is what actually gets a
// balance onto /irontraps: `INITIATE` is a Settings-only dispatch (page mount,
// checkout success, monthly-limit success) and
// `FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS` is partner-only (the epic that
// emits it is filtered on `selectPartner`), so without it a non-MSP tenant never
// requested billing/status outside /settings. Same trigger `getAgentTokenEpic`
// uses; unlike that epic we do NOT need a token, so we listen to the report
// directly instead of going through the token round-trip.
const getBillingStatusEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    // eslint-disable-next-line ironscales/fetch-selected-company-filter-guard -- billing/status is statically mounted and reachable on /irontraps as well as /settings (see comment above), so it must follow the company filter on every route, not one page
    ofType(
      GET_BILLING_STATUS_ASYNC.INITIATE,
      FETCH_APP_DATA_ASYNC.SUCCESS,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      REPORT_REQUEST_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_APP_DATA_ASYNC.SUCCESS) {
        return !selectIsStaticSalesDemo(state$.value)
      }
      if (action.type !== REPORT_REQUEST_ASYNC.SUCCESS) return true
      const state = state$.value
      const ff = selectActualFeatureFlags(state)
      return (
        !selectIsStaticSalesDemo(state) &&
        Boolean(
          ff?.ENABLE_THEMIS_AI_TRIAL_CAMPAIGN ||
          (ff?.ENABLE_THEMIS_AI_ANALYSIS && ff?.phishSoc) ||
          ff?.ENABLE_THEMIS_AI_ANALYSIS_DASHBOARD ||
          ff?.POV_COSTUMER_FF
        )
      )
    }),
    switchMap(() => {
      const companyFilter = selectCompanyFilter(state$.value)?.value
      return api.creditManagement.getBillingStatus(companyFilter).pipe(
        map((response) => GET_BILLING_STATUS_ASYNC.onSuccess(beautifyObjectKeys(response))),
        catchError((error) =>
          of(epicsErrorHandler(GET_BILLING_STATUS_ASYNC.onError, error, [404]))
        ),
        takeUntil(action$.pipe(ofType(GET_BILLING_STATUS_ASYNC.CANCEL)))
      )
    })
  )

const getPhishingSocTrialEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    // eslint-disable-next-line ironscales/fetch-selected-company-filter-guard -- the trial-status banner is statically mounted and must follow the company filter on every route, not one page
    ofType(
      GET_PHISHING_SOC_TRIAL_ASYNC.INITIATE,
      FETCH_APP_DATA_ASYNC.SUCCESS,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter(() => {
      const state = state$.value
      const ff = selectActualFeatureFlags(state)
      const isStaticDemo = selectIsStaticSalesDemo(state)
      return !isStaticDemo && ff?.ENABLE_THEMIS_AI_TRIAL_CAMPAIGN
    }),
    mergeMap(() => {
      const companyFilter = selectCompanyFilter(state$.value)
      return api.agents.getAgentTrialStatus(companyFilter?.value).pipe(
        map((response) => GET_PHISHING_SOC_TRIAL_ASYNC.onSuccess(response)),
        catchError((error) => of(epicsErrorHandler(GET_PHISHING_SOC_TRIAL_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(GET_PHISHING_SOC_TRIAL_ASYNC.CANCEL)))
      )
    })
  )

const enablePhishingSocTrialEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(ENABLE_PHISHING_SOC_TRIAL_ASYNC.INITIATE),
    mergeMap(() => {
      const companyFilter = selectCompanyFilter(state$.value)
      return api.agents.activateAgentTrial(companyFilter?.value).pipe(
        map((response) => {
          // Map TrialStatusResponse to expected format
          const trialData = response.response || response
          const mappedResponse = {
            showMeBanner: trialData?.showMeBanner ?? false,
            getPriceBanner: trialData?.getPriceBanner ?? false,
          }
          return ENABLE_PHISHING_SOC_TRIAL_ASYNC.onSuccess(mappedResponse)
        }),
        catchError((error) =>
          of(epicsErrorHandler(ENABLE_PHISHING_SOC_TRIAL_ASYNC.onError, error))
        ),
        takeUntil(action$.pipe(ofType(ENABLE_PHISHING_SOC_TRIAL_ASYNC.CANCEL)))
      )
    })
  )

const disablePhishingSocTrialEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DISABLE_PHISHING_SOC_TRIAL_ASYNC.INITIATE),
    mergeMap((action) => {
      const companyFilter = selectCompanyFilter(state$.value)
      const showToast = action.payload?.showToast !== false // Default to true for backward compatibility
      return api.agents.deactivateAgentTrial(companyFilter?.value).pipe(
        mergeMap((response) => {
          const actions = [DISABLE_PHISHING_SOC_TRIAL_ASYNC.onSuccess(response)]
          if (showToast) {
            actions.push(
              addToast({
                id: 'disable-phishing-soc-trial-success',
                title: 'Your request has been successfully submitted.',
                message: 'Our team will reach out to you shortly',
                type: 'neutral',
                timeToDisappear: 5000,
                icon: FillSparkle,
              })
            )
          }
          return of(...actions)
        }),
        catchError((error) =>
          of(epicsErrorHandler(DISABLE_PHISHING_SOC_TRIAL_ASYNC.onError, error))
        ),
        takeUntil(action$.pipe(ofType(DISABLE_PHISHING_SOC_TRIAL_ASYNC.CANCEL)))
      )
    })
  )

const requestThemisSummaryEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(REQUEST_THEMIS_SUMMARY_ASYNC.INITIATE),
    filter(() => !selectIsStaticSalesDemo(state$.value)),
    mergeMap(() => {
      const state = state$.value
      const companyFilter = selectCompanyFilter(state)
      return api.agents.getIncidentIdWithThemisSummary(companyFilter?.value).pipe(
        mergeMap((response) => {
          const incidentsData = response.response || response
          return of(REQUEST_THEMIS_SUMMARY_ASYNC.onSuccess(incidentsData.report_id))
        }),
        catchError((error) => of(epicsErrorHandler(REQUEST_THEMIS_SUMMARY_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(REQUEST_THEMIS_SUMMARY_ASYNC.CANCEL)))
      )
    })
  )

const getAtoCampaignBannerEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    // eslint-disable-next-line ironscales/fetch-selected-company-filter-guard -- the ATO campaign banner is statically mounted and must follow the company filter on every route, not one page
    ofType(
      GET_ATO_CAMPAIGN_BANNER_ASYNC.INITIATE,
      FETCH_APP_DATA_ASYNC.SUCCESS,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter(() => !selectIsStaticSalesDemo(state$.value)),
    mergeMap(() => {
      const companyFilter = selectCompanyFilter(state$.value)
      return api.atoCampaign.getAtoCampaignBanner(companyFilter?.value).pipe(
        map((response) => {
          const data = response?.response ?? response
          return GET_ATO_CAMPAIGN_BANNER_ASYNC.onSuccess(data)
        }),
        catchError((error) => of(epicsErrorHandler(GET_ATO_CAMPAIGN_BANNER_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(GET_ATO_CAMPAIGN_BANNER_ASYNC.CANCEL)))
      )
    })
  )

const postAtoCampaignContactMeEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(POST_ATO_CAMPAIGN_CONTACT_ME_ASYNC.INITIATE),
    mergeMap(() => {
      const companyFilter = selectCompanyFilter(state$.value)
      return api.atoCampaign.postAtoCampaignContactMe(companyFilter?.value).pipe(
        mergeMap((response) => {
          const data = response?.response ?? response
          return of(
            POST_ATO_CAMPAIGN_CONTACT_ME_ASYNC.onSuccess(data),
            GET_ATO_CAMPAIGN_BANNER_ASYNC.onSuccess({ showMeBanner: false })
          )
        }),
        catchError((error) =>
          of(epicsErrorHandler(POST_ATO_CAMPAIGN_CONTACT_ME_ASYNC.onError, error))
        ),
        takeUntil(action$.pipe(ofType(POST_ATO_CAMPAIGN_CONTACT_ME_ASYNC.CANCEL)))
      )
    })
  )

const deleteAtoCampaignBannerEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_ATO_CAMPAIGN_BANNER_ASYNC.INITIATE),
    mergeMap(() => {
      const companyFilter = selectCompanyFilter(state$.value)
      return api.atoCampaign.deleteAtoCampaignBanner(companyFilter?.value).pipe(
        mergeMap((response) => {
          const data = response?.response ?? response
          return of(
            DELETE_ATO_CAMPAIGN_BANNER_ASYNC.onSuccess(data),
            GET_ATO_CAMPAIGN_BANNER_ASYNC.onSuccess({ showMeBanner: false })
          )
        }),
        catchError((error) =>
          of(epicsErrorHandler(DELETE_ATO_CAMPAIGN_BANNER_ASYNC.onError, error))
        ),
        takeUntil(action$.pipe(ofType(DELETE_ATO_CAMPAIGN_BANNER_ASYNC.CANCEL)))
      )
    })
  )

export default combineEpicsIsolated(
  getAgentTokenEpic,
  generateAgentStatusTokenEpic,
  getAgentStatusBatchEpic,
  getBillingStatusEpic,
  getPhishingSocTrialEpic,
  enablePhishingSocTrialEpic,
  disablePhishingSocTrialEpic,
  requestThemisSummaryEpic,
  getAtoCampaignBannerEpic,
  postAtoCampaignContactMeEpic,
  deleteAtoCampaignBannerEpic
)
